/**
 * useSmoothScroll Hook
 * 
 * This hook initializes and manages the Lenis smooth scrolling library for the application.
 * Lenis provides high-performance smooth scrolling with minimal configuration.
 * 
 * Features:
 * - Creates and initializes a Lenis instance with optimized settings
 * - Sets up the animation frame loop for smooth animation
 * - Properly cleans up resources when the component unmounts
 * - Stores the Lenis instance in a global utility for access anywhere in the app
 * 
 * Usage in components:
 * ```tsx
 * // At the app root level (already implemented in App.tsx)
 * function App() {
 *   useSmoothScroll();
 *   // ... rest of component
 * }
 * ```
 * 
 * To programmatically control scrolling, use the utility functions from utils/lenis:
 * ```tsx
 * import { scrollTo, stop, start } from '../utils/lenis';
 * 
 * // Scroll to an element
 * scrollTo('#element-id');
 * 
 * // Scroll to position
 * scrollTo(500);
 * 
 * // Scroll with options
 * scrollTo('#element-id', { duration: 2, lock: true });
 * 
 * // Stop/start scrolling
 * stop();
 * start();
 * ```
 * 
 * @returns {React.RefObject<Lenis>} A ref containing the Lenis instance
 */
import { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import { setLenisInstance } from '../utils/lenis';

export function useSmoothScroll() {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    // Initialize Lenis with minimal configuration
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    // Store the Lenis instance in our global utility
    setLenisInstance(lenisRef.current);

    // Function to handle RAF for Lenis
    const raf = (time: number) => {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    };

    // Start the animation frame
    requestAnimationFrame(raf);

    // Clean up
    return () => {
      if (lenisRef.current) {
        lenisRef.current.destroy();
        lenisRef.current = null;
        setLenisInstance(null);
      }
    };
  }, []);

  return lenisRef;
} 